import React, {useEffect, useState} from "react";
import {useNavigate, useParams, useLocation} from "react-router-dom";
import {useForm, SubmitHandler, FormProvider} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import * as yup from "yup";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import {useAuth} from "hooks/useAuth";
import {useServerErrors} from "hooks/useServerErrors";
import BoxContent from "shared/ui/BoxContent";
import PageTitle from "shared/ui/PageTitle";
import Loading from "shared/ui/Loading";
import FormField from "shared/form/FormField";
import CheckBoxField from "shared/form/CheckBoxField";
import ErrorsList from "shared/ui/ErrorsList";
import FormLabelBox from "shared/ui/FormLabelBox";
import FormFieldBox from "shared/ui/FormFieldBox";
import GenericButton from "shared/ui/GenericButton";
import CustomButton from "shared/ui/CustomButton";
import {APIGetAddress} from "../api/address";
import {APIUpdateAddress, APIPostAddress} from "../api/address";

interface IFormAddressEdit {
    name: string;
    phone: string;
    city: string;
    county: string;
    country: string;
    line1: string;
    postal_code: string;
    isCompany: boolean;
    company_name?: string;
    company_fiscal_code?: string;
    company_registration?: string;
}

const DEFAULT_ADDRESS = {
    name: "", phone: "", city: "", county: "", country: "", line1: "",
    postal_code: "", is_company: false, company_name: undefined,
    company_fiscal_code: undefined, company_registration: undefined,
};

const schema = yup
    .object({})
    .required();

const Address = () => {
    const {id = "", action} = useParams();
    const {update} = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const methods = useForm<IFormAddressEdit>({resolver: yupResolver(schema),});
    const { handleServerErrors } = useServerErrors<IFormAddressEdit>(methods.setError);

    const [loading, setLoading] = useState<boolean>(true);
    const [editMode, setEditMode] = useState<boolean>(true);
    const [address, setAddress] = useState<any>();
    const [isCompany, setIsCompany] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string>("");

    useEffect(() => {
        if (id !== "") {
            setLoading(true);
            APIGetAddress({id})
                .then((response) => {
                    if (response.data.success) {
                        const item = response.data.data.item;
                        setAddress(item);
                        setIsCompany(item.is_company);
                    } else {
                        update("error", {show: true, code: "A002"});
                    }
                })
                .catch(() => {update("error", {show: true, code: "A001"})})
                .finally(() => setLoading(false));
        } else if (location.pathname.endsWith("adauga")) {
            setAddress(DEFAULT_ADDRESS);
            setEditMode(true);
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        if (id !== "") {
            methods.clearErrors();
            setEditMode(action === "editare");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [action, id]);

    const handleErrors = (err: any) => {
        if (err?.response?.status === 400) {
            let errors = err.response.data.data;
            handleServerErrors(errors);
        } else {
            update("error", {show: true, code: "A001"});
        }
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setIsCompany(event.target.checked);
    };

    const onSubmit: SubmitHandler<IFormAddressEdit> = (data) => {
        const payload = { ...data, is_company: isCompany };

        const hasChanges = Object.keys(payload).some(key => payload[key as keyof typeof payload] !== address[key]);
        if (!hasChanges) {
            setErrorMessage("Nu ați modificat niciun câmp.");
            return;
        }

        methods.clearErrors();
        setErrorMessage("");
        update("loading", { show: true, modal: true });

        const apiCall = id ? APIUpdateAddress(id, payload) : APIPostAddress(payload);

        apiCall
            .then((response) => {
                if (response.data.success) {
                    setAddress(data);
                    setEditMode(false);
                    navigate(-1);
                } else {
                    update("error", { show: true, code: "A002" });
                }
            })
            .catch(handleErrors)
            .finally(() => update("loading", {show: false}));
    };

    const renderAddressField = (
        fieldName: keyof typeof address,
        label: string,
        fieldType: 'text' | 'checkbox' = 'text'
    ) => {
        const errorMessage = methods.formState.errors[fieldName as keyof typeof methods.formState.errors]?.message;
        const fieldNameStr = String(fieldName);

        return (
            <>
                <FormLabelBox title={`${label}:`} />
                <FormFieldBox>
                    {!editMode ? (
                        address[fieldName]
                    ) : fieldType === 'checkbox' ? (
                        <CheckBoxField
                            checked={fieldName === 'is_company' ? isCompany : address[fieldName]}
                            onChange={fieldName === 'is_company' ? handleChange : undefined}
                            name={fieldNameStr}
                            defaultValue={address[fieldName]}
                            errorMessage={errorMessage}
                        />
                    ) : (
                        <FormField
                            name={fieldNameStr}
                            type={fieldType}
                            defaultValue={address[fieldName] as string}
                            label={label}
                            autoFocus={fieldName === 'name'}
                            errorMessage={errorMessage}
                        />
                    )}
                </FormFieldBox>
            </>
        );
    };

    return (
        <Box component="main">
            <PageTitle
                back={true}
                actions={
                    editMode ? (
                        <GenericButton type="close" onClick={() => {navigate(-1)}} />
                    ) : (
                        <GenericButton
                            type="edit"
                            onClick={() => {navigate(`/profil/adrese/${id}/editare`)}}
                        />
                    )
                }
            >
                {id !== "" ? <>Editează adresa</> : <>Adaugă adresă</>}
            </PageTitle>
            <BoxContent>
                {loading ? (
                    <Loading show={true}/>
                ) : (
                    <Box width="100%">
                        <FormProvider {...methods}>
                            <form onSubmit={methods.handleSubmit(onSubmit)} noValidate>
                                <Grid container spacing={1.5} alignItems="center">
                                    {renderAddressField('name', 'Nume')}
                                    {renderAddressField('phone', 'Telefon')}
                                    {renderAddressField('city', 'Oraș')}
                                    {renderAddressField('county', 'Județ')}
                                    {renderAddressField('country', 'Țară')}
                                    {renderAddressField('line1', 'Stradă')}
                                    {renderAddressField('postal_code', 'Cod poștal')}
                                    {renderAddressField('is_company', 'Companie?', 'checkbox')}
                                    {isCompany && (
                                        <>
                                            {renderAddressField('company_name', 'Nume companie')}
                                            {renderAddressField('company_fiscal_code', 'CUI')}
                                            {renderAddressField('company_registration', 'Număr înregistrare')}
                                        </>
                                    )}
                                    {editMode && (
                                        <>
                                            <FormLabelBox/>
                                            <FormFieldBox>
                                                <CustomButton submit buttonType="textIcon" bgcolor="dark">
                                                    {id !== "" ? "SALVEAZĂ ADRESA" : "ADAUGĂ ADRESA"}
                                                </CustomButton>
                                                <ErrorsList show={errorMessage !== ""}>{errorMessage}</ErrorsList>
                                            </FormFieldBox>
                                        </>
                                    )}
                                </Grid>
                            </form>
                        </FormProvider>
                    </Box>
                )}
            </BoxContent>
        </Box>
    );
};

export default Address;
